import Skeleton from 'react-loading-skeleton'
import {LinkWrapper} from '~/design-system/foundations'
import {HgHoverCard} from '~/design-system/hg/components'
import {unreachableCase} from '~/design-system/utils'
import IconCell from './_components/IconCell'
import TagCell from './_components/TagCell'
import TextContentCell from './_components/TextContentCell'
import {TableCell} from '~/design-system/foundations/Table'
import {tableCellVariants} from './variants'
import {type HgTableBodyCellContentProps, type HgTableBodyCellProps} from './types'

const HgTableBodyCellContent = ({
  isLoading,
  hoverCardProps,
  classNames,
  ...props
}: HgTableBodyCellContentProps) => {
  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center">
        <Skeleton containerClassName="flex-1" />
      </div>
    )
  }

  const getCellContent = () => {
    switch (props.type) {
      case 'icon':
        return <IconCell className={classNames?.content} {...props} />
      case 'tags':
        return <TagCell className={classNames?.content} {...props} />
      case 'text':
        return <TextContentCell classNames={classNames} {...props} />
      case 'empty':
        return null
      default:
        unreachableCase(props)
    }
  }

  if (hoverCardProps) {
    return (
      <HgHoverCard
        {...hoverCardProps}
        // Avoid collision with nav + sticky filter header at widest breakpoint
        collisionPadding={136}
        trigger={
          <LinkWrapper
            className="relative z-[1] rounded outline-2 outline-offset-4 outline-border-focus focus-visible:outline"
            href={hoverCardProps.href}
            data-analytics-event-name={hoverCardProps.analyticsEventName}
          >
            {getCellContent()}
          </LinkWrapper>
        }
      />
    )
  }

  return getCellContent()
}

export const HgTableBodyCell = (props: HgTableBodyCellProps) => {
  const {cell, contentWrapper} = tableCellVariants({alignment: props.alignment})

  return (
    <TableCell ref={props.ref} className={cell({className: props.classNames?.cell})}>
      <HgTableBodyCellContent
        {...props}
        classNames={{
          content: contentWrapper({className: props.classNames?.content}),
          innerTextContent: props.classNames?.innerTextContent,
        }}
      />
    </TableCell>
  )
}
